import { AdminMembersReportAssetUsagesTableRowFragment$key } from "@/admin/insights/member-reports/tabs/contents/__generated__/AdminMembersReportAssetUsagesTableRowFragment.graphql"
import { useLabel } from "@/core/context/LabelsContext"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoIcon, DiscoTable, DiscoText, DiscoTooltip } from "@disco-ui"
import DiscoTag from "@disco-ui/tag/DiscoTag"
import { TableCell } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { graphql, useFragment } from "react-relay"

interface Props {
  assetUsageKey: AdminMembersReportAssetUsagesTableRowFragment$key
}

function AdminMembersReportAssetUsagesTableRow({ assetUsageKey }: Props) {
  const classes = useStyles()
  const organizationLabel = useLabel("organization")
  const assetUsage = useFragment<AdminMembersReportAssetUsagesTableRowFragment$key>(
    graphql`
      fragment AdminMembersReportAssetUsagesTableRowFragment on AssetUsage
      @argumentDefinitions(organizationMembershipId: { type: "ID!" }) {
        id
        asset {
          name
        }
        content {
          name
          type
          contentLabelId
          usages {
            edges {
              node {
                id
                entity
                product {
                  name
                }
                module {
                  isCurriculumModule
                  isCollectionModule
                  name
                }
              }
            }
            totalCount
          }
        }
        highestAssetVideoProgress(organizationMembershipId: $organizationMembershipId) {
          maxPercent
        }
      }
    `,
    assetUsageKey
  )

  const contentUsages = Relay.connectionToArray(assetUsage.content?.usages)

  function ContentLocation({
    productName,
    module,
  }: {
    productName?: string
    module?: {
      isCurriculumModule: boolean
      isCollectionModule: boolean
      name?: string | null
    } | null
  }) {
    return (
      <div>
        <div>
          <DiscoText variant={"body-sm-500"} truncateText={2}>
            {productName || organizationLabel.singular}
          </DiscoText>
        </div>
        <div>
          <DiscoText variant={"body-xs"} color={"groovy.grey.500"} truncateText={2}>
            {module
              ? module.isCurriculumModule
                ? `Module: ${module.name}`
                : `Folder: ${module.name}`
              : "Dashboard"}
          </DiscoText>
        </div>
      </div>
    )
  }

  return (
    <DiscoTable.Row>
      {/* Name */}
      <TableCell>
        <div className={classes.iconAndNameContainer}>
          <span className={classes.iconContainer}>
            <DiscoIcon icon={"iconsax.video-square"} />
          </span>
          <div>
            <div>
              <DiscoText variant={"body-sm-500"} truncateText={1}>
                {assetUsage.asset.name}
              </DiscoText>
            </div>
            <div>
              <DiscoText variant={"body-xs"} truncateText={1}>{`in ${
                assetUsage.content!.name
              }`}</DiscoText>
            </div>
          </div>
        </div>
      </TableCell>

      {/* Content Location */}
      <TableCell>
        {contentUsages.length === 1 ? (
          <ContentLocation
            productName={contentUsages[0].product?.name}
            module={contentUsages[0].module}
          />
        ) : (
          <DiscoTooltip
            placement={"right"}
            content={contentUsages.map((contentUsage) => (
              <ContentLocation
                key={contentUsage.id}
                productName={contentUsage.product?.name}
                module={contentUsage.module}
              />
            ))}
          >
            <span>
              <DiscoTag name={`${assetUsage.content?.usages.totalCount} places`} />
            </span>
          </DiscoTooltip>
        )}
      </TableCell>

      {/* Watched */}
      <TableCell>{assetUsage.highestAssetVideoProgress ? "Yes" : "No"}</TableCell>

      {/* Watch Progress */}
      <TableCell>
        {assetUsage.highestAssetVideoProgress
          ? `${Math.round(assetUsage.highestAssetVideoProgress.maxPercent * 100)}%`
          : "-"}
      </TableCell>
    </DiscoTable.Row>
  )
}

const useStyles = makeUseStyles((theme) => ({
  iconAndNameContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  iconContainer: {
    backgroundColor: theme.palette.groovy.neutral[100],
    borderRadius: theme.measure.borderRadius.medium,
    padding: theme.spacing(1),
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
  },
}))

export function AdminMembersReportAssetUsagesTableRowSkeleton() {
  return (
    <DiscoTable.Row>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
    </DiscoTable.Row>
  )
}

export default AdminMembersReportAssetUsagesTableRow
